// campaign-view.ts — the Research → Campaigns screen's pure decisions (no React, no network): what each panel shows.
// Tested by test/campaign-view.test.ts.
import type { CampaignKeyword, HarvestBoard, CampaignCandidate, PromoteQueueEntry, ResearchCraft } from "@vms/contracts";

export const LAYER_ROWS: Array<{ layer: CampaignKeyword["layer"]; label: string }> = [
  { layer: "vertical_anchor", label: "Vertical" }, { layer: "growth_format", label: "Formats" },
  { layer: "hook_phrase", label: "Hooks" }, { layer: "negative", label: "Never" },
];
/** The chips in their four rows, in the operator's order. */
export function chipRows(keywords: readonly CampaignKeyword[]) {
  return LAYER_ROWS.map((r) => ({ ...r, chips: keywords.filter((k) => k.layer === r.layer) }));
}
/** What a chip maps to, as one short tag ("pattern · day in the life trust"). */
export function mapsToTag(k: CampaignKeyword): string | null {
  if (!k.mapsTo) return null;
  const kind = { story_pattern: "pattern", viral_type: "type", preset: "preset", hook_archetype: "hook" }[k.mapsTo.kind];
  return `${kind} · ${k.mapsTo.id.replace(/_/g, " ")}`;
}

/** The funnel as its stages, left to right — the board's one row of counts. */
export function funnelStages(f: HarvestBoard["funnel"]) {
  return [["harvested", f.harvested], ["scored", f.scored], ["kept", f.kept], ["downloaded", f.downloaded], ["queued", f.queued], ["torn down", f.tornDown]] as Array<[string, number]>;
}

/** The board never pretends a harvest is running (VMS-RESEARCH-CAMPAIGN-UI §4). One line, or null when all is well. */
export function workerBanner(b: HarvestBoard, vertical: string, status: string): string | null {
  if (status === "paused") return "Paused — no keyword of this campaign is claimable.";
  if (status === "draft") return null;
  const pinned = b.workers.pinned.length, free = b.workers.unpinnedOnline;
  const v = vertical.replace(/_/g, " ");
  const auto = b.workers.autopilotOnNiche ? ` · ${b.workers.autopilotOnNiche} on Autopilot — its harvests count here too` : "";
  if (!pinned && !free) return `No worker online for ${v}. Start an extension profile pinned to “${vertical}”.`;
  if (!pinned) return `No worker pinned to ${v} — ${free} unpinned worker${free === 1 ? "" : "s"} can claim these keywords${auto}.`;
  return auto ? auto.slice(3) : null;
}

/** Follows-per-view as the candidate card shows it — the PROXY is named for what it is (answers D3). */
export function fpvLabel(c: Pick<CampaignCandidate, "fpv" | "fpvSource">): { text: string; tone: "measured" | "proxy" | "none" } {
  if (c.fpv == null || !c.fpvSource) return { text: "no follows-per-view yet", tone: "none" };
  const per1k = (c.fpv * 1000).toFixed(c.fpv * 1000 < 10 ? 1 : 0);
  return c.fpvSource === "measured"
    ? { text: `${per1k} follows / 1k views`, tone: "measured" }
    : { text: `${per1k} / 1k — account conversion, not this video's`, tone: "proxy" };
}
/** The card's short tag: the number + which tier ("20 · measured" / "1.2 · account"); the header states what "account" means. */
export function fpvTag(c: Pick<CampaignCandidate, "fpv" | "fpvSource">): string {
  if (c.fpv == null || !c.fpvSource) return "—";
  const per1k = (c.fpv * 1000).toFixed(c.fpv * 1000 < 10 ? 1 : 0);
  return `${per1k} · ${c.fpvSource === "measured" ? "measured" : "account"}`;
}
export const compact = (n: number | null | undefined) =>
  n == null ? "—" : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}k` : String(n);

/** One line for a craft chip ("lighting · practical, high"). */
export function craftLine(c: ResearchCraft): string {
  const e = c.ext as Record<string, unknown>;
  const pick = (...k: string[]) => k.map((x) => e[x]).filter((v) => v != null && v !== "none" && v !== "any" && v !== "ambient").map(String).join(", ").replace(/_/g, " ");
  const body = c.kind === "lighting" ? pick("sourceType", "intensity", "contrast")
    : c.kind === "camera_movement" ? pick("axis", "direction", "speed")
    : c.kind === "composition" ? pick("rule", "subjectPlacement")
    : c.kind === "camera_shot" ? pick("framing")
    : c.kind === "camera_angle" ? pick("azimuth")
    : `${e.secondsPerCutTypical} s cuts, ${e.variance}`;
  return `${c.kind.replace(/_/g, " ")} · ${body}`;
}
export const craftKey = (c: Pick<ResearchCraft, "kind" | "shotIndex">) => `${c.kind}|${c.shotIndex ?? "video"}`;
/** The default selection: everything the teardown DERIVED something for; wholly-defaulted crafts start unticked. */
export function defaultSelection(e: PromoteQueueEntry): Set<string> {
  return new Set(e.crafts.filter((c) => c.derived.length > 0).map(craftKey));
}
